import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Service Area', path: '/service-area' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header
      style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white py-3'
          : 'bg-white py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo — prominent on all sizes */}
        <Link to="/" className="shrink-0">
          <svg style={{ width: '180px', height: 'auto' }} version="1.0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.534615 29.646062" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="logoGradientHeader" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#192255"/>
                <stop offset="35%" stopColor="#1b3996"/>
                <stop offset="40%" stopColor="#2d6abe"/>
                <stop offset="50%" stopColor="#3b96e7"/>
                <stop offset="100%" stopColor="#3c9de9"/>
              </linearGradient>
            </defs>
            <g transform="translate(-0.120000,29.729449) scale(0.024000,-0.024000)" fill="url(#logoGradientHeader)" stroke="none">
              <path d="M1051 1231 c-66 -7 -54 28 -126 -361 -31 -162 -62 -330 -70 -373 -39 -202 -4 -277 130 -277 66 0 123 26 154 69 25 35 27 56 5 47 -71 -27 -94 -14 -94 52 0 25 25 177 56 337 30 159 59 313 64 340 5 28 13 70 19 95 18 78 17 80 -36 78 -27 -1 -72 -4 -102 -7z"/>
              <path d="M545 1206 c-138 -32 -238 -88 -341 -191 -69 -69 -138 -183 -170 -281 -26 -80 -29 -102 -29 -224 0 -133 1 -137 33 -202 38 -78 82 -120 171 -161 60 -28 73 -31 175 -31 104 0 117 2 196 34 69 28 97 46 145 95 58 58 95 118 81 132 -4 4 -13 3 -20 -2 -113 -85 -199 -119 -301 -119 -161 1 -237 106 -222 304 23 285 206 534 380 517 35 -3 56 -11 75 -31 37 -37 36 -110 -2 -184 -15 -30 -25 -56 -22 -59 13 -13 103 34 142 74 76 78 96 190 46 260 -47 66 -207 99 -337 69z"/>
              <path d="M3386 1158 c-6 -27 -74 -403 -118 -648 -17 -96 -36 -178 -42 -182 -17 -12 -71 -9 -84 4 -18 18 -14 85 14 240 29 156 28 224 -3 266 -62 85 -214 83 -328 -4 l-36 -27 7 42 7 41 -102 0 c-73 0 -103 -4 -105 -12 -2 -7 -18 -92 -36 -188 -28 -157 -52 -282 -66 -349 -3 -13 -7 -21 -10 -18 -3 3 -18 1 -34 -5 -20 -6 -35 -6 -49 2 -31 16 -27 80 20 323 22 115 38 215 35 222 -7 20 -163 38 -289 33 -138 -4 -199 -29 -283 -112 -90 -91 -130 -203 -125 -355 4 -139 70 -211 193 -211 66 0 125 26 175 78 l38 39 18 -37 c24 -51 73 -74 154 -73 43 0 75 7 103 22 39 20 40 20 40 1 0 -19 7 -20 100 -20 71 0 102 4 105 12 3 7 16 74 30 149 39 213 44 234 78 290 36 61 84 91 130 82 50 -10 53 -36 23 -200 -41 -232 -33 -283 51 -324 52 -25 137 -23 191 5 28 15 32 15 32 1 0 -12 18 -15 109 -15 l109 0 10 48 c5 26 22 118 38 205 l28 157 166 0 167 0 -23 -122 c-12 -68 -29 -158 -37 -201 -11 -61 -12 -80 -3 -83 7 -3 57 -3 112 -2 l99 3 42 230 c23 127 61 332 84 456 23 125 39 234 35 244 -6 16 -16 17 -109 11 -56 -4 -102 -8 -103 -9 -2 -1 -18 -92 -38 -201 -20 -113 -41 -203 -48 -208 -15 -10 -304 -6 -314 5 -4 4 7 82 24 174 18 93 33 184 34 203 l3 35 -107 3 c-105 3 -108 2 -112 -20z m-1157 -367 c16 -10 10 -54 -25 -190 -50 -193 -147 -300 -209 -231 -52 57 -9 295 68 379 40 43 128 66 166 42z"/>
              <path d="M6850 988 c-12 -68 -42 -233 -67 -368 -53 -293 -63 -349 -63 -372 0 -16 11 -18 95 -18 61 0 95 4 95 11 0 15 91 523 94 526 4 4 256 -515 256 -527 0 -6 38 -10 95 -10 90 0 95 1 99 22 2 13 13 77 25 143 12 66 44 246 71 400 28 154 50 287 50 297 0 16 -10 18 -89 18 -84 0 -89 -1 -94 -22 -11 -48 -49 -259 -70 -393 l-23 -140 -60 125 c-33 69 -94 194 -134 278 l-74 152 -92 0 -92 0 -22 -122z"/>
              <path d="M7802 1058 c-6 -29 -26 -143 -46 -253 -20 -110 -50 -276 -67 -369 -17 -93 -39 -187 -50 -208 -38 -74 -115 -101 -211 -74 -33 9 -63 15 -65 12 -10 -10 -31 -122 -24 -129 4 -4 41 -14 82 -23 178 -38 343 29 414 166 32 63 50 131 80 305 14 83 32 182 40 220 8 39 26 138 40 220 15 83 29 158 31 168 5 15 -5 17 -105 17 l-110 0 -9 -52z"/>
              <path d="M1433 880 c-96 -34 -175 -111 -226 -218 -30 -63 -32 -74 -32 -177 0 -119 10 -153 63 -203 49 -45 100 -62 192 -62 69 0 95 5 136 24 53 25 119 85 130 118 9 28 -11 46 -34 30 -72 -50 -151 -71 -206 -52 -42 14 -64 46 -73 105 l-6 45 39 0 c50 0 156 25 197 46 96 50 146 120 147 205 0 60 -31 107 -90 137 -57 28 -162 30 -237 2z m162 -142 c0 -51 -4 -61 -34 -92 -37 -38 -77 -60 -128 -71 -30 -6 -33 -4 -33 18 0 13 16 56 35 95 40 79 81 114 130 110 30 -3 30 -4 30 -60z"/>
              <path d="M4347 876 c-144 -60 -240 -208 -241 -376 -1 -107 19 -164 79 -219 55 -51 105 -64 220 -59 76 3 98 8 143 33 169 92 255 333 181 505 -26 60 -54 88 -114 116 -70 33 -186 33 -268 0z m177 -89 c71 -52 7 -386 -87 -452 -12 -8 -38 -15 -59 -15 -50 0 -68 34 -68 127 0 124 48 261 111 318 41 37 73 44 103 22z"/>
              <path d="M5667 882 c-103 -37 -156 -115 -142 -208 9 -61 62 -114 174 -174 91 -48 117 -76 107 -115 -15 -61 -139 -72 -238 -21 -27 14 -54 26 -58 26 -5 0 -12 -16 -16 -35 -11 -61 -84 -73 -105 -18 -11 30 -12 28 42 323 21 118 39 218 39 222 0 4 -47 8 -103 8 l-104 0 -12 -72 c-33 -193 -63 -332 -77 -361 -37 -72 -101 -110 -146 -86 -48 25 -49 40 -3 289 19 105 35 200 35 210 0 19 -7 20 -99 20 l-99 0 -11 -50 c-40 -187 -72 -402 -69 -460 6 -112 60 -160 180 -160 72 0 126 21 180 69 l37 32 13 -27 c26 -53 61 -69 151 -69 57 1 93 6 118 18 33 16 38 16 80 1 69 -26 277 -31 335 -9 57 22 113 69 130 112 19 44 17 121 -4 160 -20 38 -84 87 -170 133 -59 31 -102 70 -102 92 0 5 11 20 25 33 20 20 34 25 77 25 29 -1 71 -8 93 -16 74 -28 71 -29 85 20 7 25 10 52 7 59 -16 42 -255 62 -350 29z"/>
              <path d="M6325 886 c-68 -21 -100 -43 -157 -104 -85 -92 -120 -208 -107 -354 7 -79 38 -134 100 -175 37 -24 53 -28 138 -31 122 -5 179 16 246 90 49 55 51 60 31 77 -11 9 -25 5 -65 -20 -44 -28 -62 -33 -116 -33 -57 -1 -68 2 -91 26 -20 19 -28 39 -32 77 l-5 51 40 0 c46 0 141 23 197 49 134 61 182 203 101 300 -47 55 -183 78 -280 47z m155 -107 c17 -31 2 -96 -31 -134 -29 -33 -75 -56 -138 -70 l-33 -7 7 33 c22 105 96 199 155 199 19 0 33 -7 40 -21z"/>
            </g>
          </svg>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`text-sm font-semibold transition-colors hover:text-brand-blue ${
                location.pathname === link.path
                  ? 'text-brand-blue'
                  : 'text-brand-navy'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center space-x-5">
          <a
            href="tel:2018700452"
            className="flex items-center text-brand-navy font-semibold text-sm hover:text-brand-blue transition-colors"
          >
            <Phone className="w-4 h-4 mr-2 text-brand-blue" />
            (201) 870-0452
          </a>
          <Link
            to="/contact"
            className="bg-brand-red text-white px-7 py-3 rounded-full text-sm font-black shadow-lg hover:bg-red-700 transition-all transform hover:scale-105"
          >
            Request Quote
          </Link>
        </div>

        {/* Mobile: phone + hamburger */}
        <div className="lg:hidden flex items-center space-x-4">
          <a
            href="tel:2018700452"
            className="flex items-center text-brand-navy font-bold text-sm"
          >
            <Phone className="w-5 h-5 text-brand-blue" />
          </a>
          <button
            className="text-brand-navy p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl border-t border-gray-100">
          <nav className="flex flex-col p-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-lg font-semibold py-3 px-4 rounded-xl transition-colors ${
                  location.pathname === link.path
                    ? 'text-brand-blue bg-brand-blue/5'
                    : 'text-brand-navy hover:bg-gray-50'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <a
                href="tel:2018700452"
                className="flex items-center text-brand-navy font-bold text-base py-2 px-4"
              >
                <Phone className="w-5 h-5 mr-3 text-brand-blue" />
                (201) 870-0452
              </a>
              <Link
                to="/contact"
                className="bg-brand-red text-white px-6 py-4 rounded-2xl text-center font-black text-lg shadow-lg block"
                onClick={() => setIsMenuOpen(false)}
              >
                Request Quote
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
